import { Box, Stack, Tooltip, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { toDataCy } from "lib/to-data-cy"
import Iconify from "minimals-template/components/Iconify"
import { formatMoney, formatNumber } from "../format-utils"

export function ComparisonRow({
    label,
    labelAction,
    icon,
    value,
    subtitle,
    bold,
    delta,
    deltaPrefix,
    deltaFormat,
    invertColor,
    onClick,
    valueTooltip,
}) {
    const clickableSx = onClick
        ? {
              cursor: "pointer",
              "&:hover": {
                  color: "primary.main",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
              },
          }
        : null
    const showValueTooltip = !!valueTooltip
    return (
        <Stack
            data-cy="comparison-row"
            direction="row"
            alignItems="center"
            sx={{
                py: bold ? 2.5 : 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-of-type": { borderBottom: 0 },
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                <If value={icon}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Iconify icon={icon} sx={{ color: "common.white", fontSize: 20 }} />
                    </Box>
                </If>
                <Stack spacing={0}>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={bold ? 700 : 500}
                        sx={{ fontSize: bold ? 18 : 16 }}
                    >
                        {label}
                    </Typography>
                    <If value={labelAction}>{labelAction}</If>
                </Stack>
            </Stack>
            <If value={bold}>
                <Stack alignItems="flex-end" justifyContent="center" spacing={0.25}>
                    <Tooltip
                        title={valueTooltip || ""}
                        disableHoverListener={!showValueTooltip}
                        disableFocusListener={!showValueTooltip}
                        disableTouchListener={!showValueTooltip}
                    >
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: 18 }}>
                            {value}
                        </Typography>
                    </Tooltip>
                    <If value={delta}>
                        <Typography
                            data-cy={toDataCy("comparison-row-delta", getDeltaDirection(delta))}
                            sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                color: getDeltaColor(delta, invertColor),
                                lineHeight: 1.5,
                            }}
                        >
                            {formatDelta(delta, deltaPrefix, deltaFormat)}
                        </Typography>
                    </If>
                </Stack>
                <Else>
                    <Stack alignItems="flex-end" justifyContent="center" sx={{ minHeight: 48 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Tooltip
                                title={valueTooltip || ""}
                                disableHoverListener={!showValueTooltip}
                                disableFocusListener={!showValueTooltip}
                                disableTouchListener={!showValueTooltip}
                            >
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    onClick={onClick}
                                    sx={{ fontSize: 16, ...clickableSx }}
                                >
                                    {value}
                                </Typography>
                            </Tooltip>
                            <If value={delta}>
                                <Typography
                                    data-cy={toDataCy("comparison-row-delta", getDeltaDirection(delta))}
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        whiteSpace: "nowrap",
                                        color: getDeltaColor(delta, invertColor),
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {formatDelta(delta, deltaPrefix, deltaFormat)}
                                </Typography>
                            </If>
                        </Stack>
                        <If value={subtitle}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>
                                {subtitle}
                            </Typography>
                        </If>
                    </Stack>
                </Else>
            </If>
        </Stack>
    )
}

function getDeltaColor(delta, invertColor) {
    if (!delta) return "transparent"
    const isNegative = delta < 0
    // Default: a decrease is "good" (success/green) and an increase is "bad" (error/red).
    // invertColor flips this for metrics where more is better (e.g. coverage, compliance).
    if (invertColor) return isNegative ? "error.main" : "success.main"
    return isNegative ? "success.main" : "error.main"
}

function getDeltaDirection(delta) {
    if (delta < 0) return "decrease"
    return "increase"
}

function formatDelta(delta, prefix, format) {
    if (!delta) return "\u00a0"
    const arrow = delta < 0 ? "\u2193" : "\u2191"
    const absVal = Math.abs(delta)
    const formatted = format === "money" ? formatMoney(absVal) : formatNumber(absVal)
    return `${arrow} ${prefix || ""}${formatted}`
}
